import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { Checkbox } from "../components/ui/checkbox";
import { Award, Mail, User, Phone, Building, Briefcase, Globe, CheckCircle, Clock, AlertCircle, Loader2 } from "lucide-react";
import { COUNTRIES } from "../lib/utils";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function ApplyPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const magicToken = searchParams.get("token");

  // States
  const [step, setStep] = useState(magicToken ? "verifying" : "choose_auth"); // choose_auth, magic_link_sent, verifying, registration_form, pending_approval, already_applied
  const [loading, setLoading] = useState(false);
  const [user, setUser] = useState(null);
  const [email, setEmail] = useState("");
  const [tokenEmail, setTokenEmail] = useState("");
  
  // Registration form
  const [formData, setFormData] = useState({
    full_name: "",
    email: "",
    phone: "",
    nationality: "",
    current_employer: "",
    current_role: "",
    declaration_accepted: false
  });

  // Verify magic link token if present
  useEffect(() => {
    if (magicToken) {
      verifyMagicToken();
    }
  }, [magicToken]);

  // Check if user is already authenticated
  useEffect(() => {
    checkExistingAuth();
  }, []);

  const checkExistingAuth = async () => {
    try {
      const response = await axios.get(`${API}/auth/me`, { withCredentials: true });
      if (response.data) {
        setUser(response.data);
        setFormData(prev => ({ ...prev, email: response.data.email }));
        
        // Check if already has a candidate application
        if (response.data.linked_candidate_id) {
          setStep("already_applied");
        } else {
          setStep("registration_form");
        }
      }
    } catch (error) {
      // Not authenticated, stay on choose_auth
    }
  };

  const verifyMagicToken = async () => {
    setLoading(true);
    try {
      // First verify the token
      const verifyResponse = await axios.get(`${API}/auth/verify-magic-link/${magicToken}`);
      
      if (verifyResponse.data.valid) {
        setTokenEmail(verifyResponse.data.email);
        
        // Complete login with the token
        const loginResponse = await axios.post(
          `${API}/auth/magic-link-login`,
          { token: magicToken },
          { withCredentials: true }
        );
        
        setUser(loginResponse.data);
        setFormData(prev => ({ 
          ...prev, 
          email: loginResponse.data.email,
          full_name: loginResponse.data.name || ""
        }));
        
        // Check if already has application
        if (loginResponse.data.linked_candidate_id) {
          setStep("already_applied");
        } else {
          setStep("registration_form");
          toast.success("Signed in successfully!");
        }
      }
    } catch (error) {
      console.error("Token verification failed:", error);
      toast.error(error.response?.data?.detail || "Magic link is invalid or expired");
      setStep("choose_auth");
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleSignIn = () => {
    // Redirect to Google OAuth with /apply as callback
    const redirectUrl = window.location.origin + '/apply';
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const handleSendMagicLink = async (e) => {
    e.preventDefault();
    if (!email) {
      toast.error("Please enter your email");
      return;
    }

    setLoading(true);
    try {
      await axios.post(`${API}/auth/magic-link`, { email });
      setStep("magic_link_sent");
      toast.success("Magic link sent to your email!");
    } catch (error) {
      console.error("Failed to send magic link:", error);
      toast.error(error.response?.data?.detail || "Failed to send magic link");
    } finally {
      setLoading(false);
    }
  };

  const handleSubmitRegistration = async (e) => {
    e.preventDefault();
    
    if (!formData.full_name || !formData.email) {
      toast.error("Name and email are required");
      return;
    }
    
    if (!formData.declaration_accepted) {
      toast.error("Please accept the declaration to proceed");
      return;
    }

    setLoading(true);
    try {
      const response = await axios.post(
        `${API}/register/self`,
        formData,
        { withCredentials: true }
      );
      
      toast.success("Application submitted successfully!");
      setStep("pending_approval");
    } catch (error) {
      console.error("Registration failed:", error);
      toast.error(error.response?.data?.detail || "Failed to submit application");
    } finally {
      setLoading(false);
    }
  };

  // Handle OAuth callback
  useEffect(() => {
    const hash = window.location.hash;
    if (hash?.includes('session_id=')) {
      const sessionId = new URLSearchParams(hash.substring(1)).get('session_id');
      if (sessionId) {
        handleOAuthCallback(sessionId);
      }
    }
  }, []);

  const handleOAuthCallback = async (sessionId) => {
    setLoading(true);
    try {
      // IMPORTANT: Pass registration_type="candidate" to tell backend this is a candidate registration
      const response = await axios.post(
        `${API}/auth/session`,
        { session_id: sessionId, registration_type: "candidate" },
        { withCredentials: true }
      );
      
      setUser(response.data);
      setFormData(prev => ({ 
        ...prev, 
        email: response.data.email,
        full_name: response.data.name || ""
      }));
      
      // Clear the hash
      window.history.replaceState(null, '', '/apply');
      
      // Check if already has application
      if (response.data.linked_candidate_id) {
        setStep("already_applied");
      } else {
        setStep("registration_form");
        toast.success("Signed in with Google successfully!");
      }
    } catch (error) {
      console.error("OAuth callback failed:", error);
      toast.error("Authentication failed");
      setStep("choose_auth");
    } finally {
      setLoading(false);
    }
  };

  // Render different steps
  const renderChooseAuth = () => (
    <Card className="w-full max-w-md mx-auto">
      <CardHeader className="text-center">
        <div className="w-16 h-16 bg-gradient-to-br from-blue-600 to-blue-800 rounded-2xl flex items-center justify-center mx-auto mb-4">
          <Award className="w-8 h-8 text-white" />
        </div>
        <CardTitle className="text-2xl">Apply for RPL Assessment</CardTitle>
        <CardDescription>
          Start your Recognition of Prior Learning journey with Credentia AI
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Google Sign In */}
        <Button 
          data-testid="google-signin-btn"
          onClick={handleGoogleSignIn}
          className="w-full h-12 text-base"
          variant="outline"
        >
          <svg className="w-5 h-5 mr-3" viewBox="0 0 24 24">
            <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
            <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
            <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
            <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
          </svg>
          Sign in with Google
        </Button>

        <div className="relative">
          <div className="absolute inset-0 flex items-center">
            <span className="w-full border-t" />
          </div>
          <div className="relative flex justify-center text-xs uppercase">
            <span className="bg-white px-2 text-slate-500">Or</span>
          </div>
        </div>

        {/* Magic Link */}
        <form onSubmit={handleSendMagicLink} className="space-y-3">
          <div className="space-y-2">
            <Label htmlFor="email">Email Address</Label>
            <div className="relative">
              <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input
                id="email"
                data-testid="magic-link-email"
                type="email"
                placeholder="your@email.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="pl-10"
                required
              />
            </div>
          </div>
          <Button 
            data-testid="send-magic-link-btn"
            type="submit" 
            className="w-full"
            disabled={loading}
          >
            {loading ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Sending...
              </>
            ) : (
              <>
                <Mail className="w-4 h-4 mr-2" />
                Send Magic Link
              </>
            )}
          </Button>
        </form>

        <p className="text-xs text-slate-500 text-center mt-4">
          We'll send you a secure link that expires in 1 hour
        </p>
      </CardContent>
    </Card>
  );

  const renderMagicLinkSent = () => (
    <Card className="w-full max-w-md mx-auto">
      <CardHeader className="text-center">
        <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
          <Mail className="w-8 h-8 text-green-600" />
        </div>
        <CardTitle className="text-2xl">Check Your Email</CardTitle>
        <CardDescription>
          We've sent a sign-in link to <strong>{email}</strong>
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
          <div className="flex items-start gap-3">
            <Clock className="w-5 h-5 text-amber-600 mt-0.5" />
            <div>
              <p className="font-medium text-amber-800">Link expires in 1 hour</p>
              <p className="text-sm text-amber-700">Click the link in your email to continue your application</p>
            </div>
          </div>
        </div>
        
        <Button 
          variant="outline" 
          className="w-full"
          onClick={() => setStep("choose_auth")}
        >
          Use a different email
        </Button>
      </CardContent>
    </Card>
  );

  const renderVerifying = () => (
    <Card className="w-full max-w-md mx-auto">
      <CardContent className="py-12 text-center">
        <Loader2 className="w-12 h-12 animate-spin text-blue-600 mx-auto mb-4" />
        <p className="text-slate-600">Verifying your sign-in link...</p>
      </CardContent>
    </Card>
  );

  const renderRegistrationForm = () => (
    <Card className="w-full max-w-2xl mx-auto">
      <CardHeader>
        <CardTitle className="text-2xl flex items-center gap-2">
          <User className="w-6 h-6" />
          Complete Your Application
        </CardTitle>
        <CardDescription>
          Please provide your information to start your RPL assessment
        </CardDescription>
      </CardHeader>
      <CardContent>
        <form onSubmit={handleSubmitRegistration} className="space-y-6">
          {/* Personal Information */}
          <div className="space-y-4">
            <h3 className="font-semibold text-slate-900 border-b pb-2">Personal Information</h3>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="full_name">Full Name *</Label>
                <div className="relative">
                  <User className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <Input
                    id="full_name"
                    data-testid="reg-full-name"
                    value={formData.full_name}
                    onChange={(e) => setFormData({ ...formData, full_name: e.target.value })}
                    className="pl-10"
                    placeholder="John Doe"
                    required
                  />
                </div>
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="email">Email Address *</Label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <Input
                    id="email"
                    data-testid="reg-email"
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    className="pl-10 bg-slate-50"
                    placeholder="your@email.com"
                    required
                    disabled={!!user?.email}
                  />
                </div>
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="phone">Phone Number</Label>
                <div className="relative">
                  <Phone className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <Input
                    id="phone"
                    data-testid="reg-phone"
                    value={formData.phone}
                    onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                    className="pl-10"
                    placeholder="+1 234 567 8900"
                  />
                </div>
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="nationality">Nationality</Label>
                <div className="relative">
                  <Globe className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-slate-400 z-10" />
                  <select
                    id="nationality"
                    data-testid="reg-nationality"
                    value={formData.nationality}
                    onChange={(e) => setFormData({ ...formData, nationality: e.target.value })}
                    className="w-full h-10 pl-10 pr-4 rounded-md border border-slate-200 bg-white text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="">Select country...</option>
                    {COUNTRIES.map((country) => (
                      <option key={country} value={country}>{country}</option>
                    ))}
                  </select>
                </div>
              </div>
            </div>
          </div>

          {/* Employment Information */}
          <div className="space-y-4">
            <h3 className="font-semibold text-slate-900 border-b pb-2">Current Employment</h3>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="current_employer">Current Employer</Label>
                <div className="relative">
                  <Building className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <Input
                    id="current_employer"
                    data-testid="reg-employer"
                    value={formData.current_employer}
                    onChange={(e) => setFormData({ ...formData, current_employer: e.target.value })}
                    className="pl-10"
                    placeholder="Company Name"
                  />
                </div>
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="current_role">Current Role/Job Title</Label>
                <div className="relative">
                  <Briefcase className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <Input
                    id="current_role"
                    data-testid="reg-role"
                    value={formData.current_role}
                    onChange={(e) => setFormData({ ...formData, current_role: e.target.value })}
                    className="pl-10"
                    placeholder="Software Engineer"
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Declaration */}
          <div className="bg-slate-50 rounded-lg p-4 space-y-3">
            <h3 className="font-semibold text-slate-900">Declaration</h3>
            <p className="text-sm text-slate-600">
              I declare that all information provided is true and accurate to the best of my knowledge. 
              I understand that my application will be reviewed by an administrator before I can upload documents.
            </p>
            <div className="flex items-center space-x-2">
              <Checkbox
                id="declaration"
                data-testid="reg-declaration"
                checked={formData.declaration_accepted}
                onCheckedChange={(checked) => setFormData({ ...formData, declaration_accepted: checked })}
              />
              <Label htmlFor="declaration" className="text-sm font-normal cursor-pointer">
                I accept the declaration above *
              </Label>
            </div>
          </div>

          <Button 
            type="submit" 
            data-testid="submit-application-btn"
            className="w-full h-12 text-base"
            disabled={loading || !formData.declaration_accepted}
          >
            {loading ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Submitting...
              </>
            ) : (
              <>
                <CheckCircle className="w-4 h-4 mr-2" />
                Submit Application
              </>
            )}
          </Button>
        </form>
      </CardContent>
    </Card>
  );

  const renderPendingApproval = () => (
    <Card className="w-full max-w-md mx-auto">
      <CardHeader className="text-center">
        <div className="w-16 h-16 bg-amber-100 rounded-full flex items-center justify-center mx-auto mb-4">
          <Clock className="w-8 h-8 text-amber-600" />
        </div>
        <CardTitle className="text-2xl">Application Submitted!</CardTitle>
        <CardDescription>
          Your application is pending admin review
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <div className="flex items-start gap-3">
            <AlertCircle className="w-5 h-5 text-blue-600 mt-0.5" />
            <div>
              <p className="font-medium text-blue-800">What happens next?</p>
              <ul className="text-sm text-blue-700 mt-2 space-y-1 list-disc list-inside">
                <li>An administrator will review your application</li>
                <li>You'll receive an email once approved</li>
                <li>After approval, you can upload your documents</li>
              </ul>
            </div>
          </div>
        </div>
        
        <Button 
          variant="outline" 
          className="w-full"
          onClick={() => navigate("/my-application")}
        >
          Go to My Dashboard
        </Button>
      </CardContent>
    </Card>
  );

  const renderAlreadyApplied = () => (
    <Card className="w-full max-w-md mx-auto">
      <CardHeader className="text-center">
        <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
          <CheckCircle className="w-8 h-8 text-green-600" />
        </div>
        <CardTitle className="text-2xl">Application Found</CardTitle>
        <CardDescription>
          You already have an application on file
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <p className="text-center text-slate-600">
          Please visit your dashboard to check your application status and upload documents.
        </p>
        
        <Button 
          className="w-full"
          onClick={() => navigate("/my-application")}
        >
          Go to My Dashboard
        </Button>
      </CardContent>
    </Card>
  );

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100">
      {/* Header */}
      <nav className="bg-white border-b border-slate-200 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-4">
            <div className="flex items-center cursor-pointer" onClick={() => navigate("/")}>
              <img 
                src="/credentia-ai-logo.png" 
                alt="Credentia AI" 
                className="h-12 w-auto object-contain"
              />
            </div>
            <div className="text-sm text-slate-500">
              Already have an account?{" "}
              <a href="/" className="text-blue-600 hover:underline">Sign In</a>
            </div>
          </div>
        </div>
      </nav>

      {/* Content */}
      <div className="py-12 px-4">
        {step === "choose_auth" && renderChooseAuth()}
        {step === "magic_link_sent" && renderMagicLinkSent()}
        {step === "verifying" && renderVerifying()}
        {step === "registration_form" && renderRegistrationForm()}
        {step === "pending_approval" && renderPendingApproval()}
        {step === "already_applied" && renderAlreadyApplied()}
      </div>

      {/* Footer */}
      <footer className="bg-white border-t border-slate-200 py-6 mt-auto">
        <div className="max-w-7xl mx-auto px-4 text-center">
          <p className="text-sm text-slate-500">
            © {new Date().getFullYear()} Credentia AI. All rights reserved.
          </p>
        </div>
      </footer>
    </div>
  );
}
